import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, AlertCircle, MessageSquare, Bot, Package, Wrench, HelpCircle } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const sections = [
  { id: 'overview', title: 'Overview' },
  { id: 'accessing-prompts', title: 'Accessing SweetPilot Prompts' },
  { id: 'plus-bundle', title: 'SweetPilot Plus Bundle' },
  { id: 'accessing-bots', title: 'Accessing SweetPilot Bots' },
  { id: 'max-bundle', title: 'SweetPilot Max Bundle' },
  { id: 'add-ons', title: 'Add-ons' },
  { id: 'troubleshooting', title: 'Troubleshooting Access Issues' },
];

const promptCategories = [
  'Agency', 'Assistants', 'Business', 'Copywriting', 'HR', 'Marketing',
  'Productivity', 'Real Estate', 'Sales', 'Startups', 'Support', 'Web Development',
];

const relatedArticles = [
  { title: 'How to navigate the SweetPilot web app', href: '/doc/article/navigate-web-app' },
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'Which AI Models does SweetPilot use?', href: '/doc/article/ai-models' },
  { title: 'Who can benefit from using SweetPilot?', href: '/doc/article/who-benefits' },
];

export default function SweetPilotLegacy() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('overview');

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'SweetPilot Legacy Products - SweetPilot Prompts & Bots Migration Guide',
    description: 'Information about SweetPilot legacy products including SweetPilot Prompts and SweetPilot Bots. Learn about migration paths and accessing legacy features.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/sweetpilot-legacy' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id),
      }));

      const currentSection = sectionElements.find(section => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>SweetPilot Legacy Products - Prompts & Bots Migration | SweetPilot Help Center</title>
        <meta name="description" content="Learn about SweetPilot legacy products including SweetPilot Prompts and SweetPilot Bots. Migration guide and how to access legacy features." />
        <meta name="keywords" content="SweetPilot legacy, SweetPilot Prompts, SweetPilot Bots, migration guide, legacy products, product transition, AI migration" />
        <meta property="og:title" content="SweetPilot Legacy Products - Migration Guide" />
        <meta property="og:description" content="Information about SweetPilot Prompts, Bots, and migration paths." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/sweetpilot-legacy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Legacy Products" />
        <meta name="twitter:description" content="Migration guide for SweetPilot Prompts and Bots." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/sweetpilot-legacy" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout title="" subtitle="" showBackButton={false}>
        {/* Breadcrumbs */}
        <nav className="mb-8">
          <ol className={`flex items-center flex-wrap gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            <li>
              <Link to="/docs" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                All Collections
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" /></li>
            <li>
              <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                Getting started
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" /></li>
            <li>
              <Link to="/doc/collections/legacy-products" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                SweetPilot AI Legacy products
            </Link>
          </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            SweetPilot Legacy
          </li>
        </ol>
      </nav>

      <div className="flex gap-10">
        {/* Main Content */}
        <div className="flex-1 max-w-3xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              SweetPilot Legacy
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Learn about SweetPilot AI legacy products: SweetPilot Bots, Prompts, Add-ons, and the legacy version of SweetPilot Employees.
            </p>
            <div className={`flex items-center gap-3 mt-4 text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                <span className="text-sm">S</span>
              </div>
              <div>
                <span>Written by </span>
                <span className={isDark ? 'text-white' : 'text-slate-900'}>Support Team</span>
              </div>
              <span>•</span>
              <span>Updated recently</span>
            </div>
          </motion.div>

          {/* EOL Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`p-5 rounded-xl border mb-8 ${
              isDark ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-6 h-6 flex-shrink-0 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
              <div>
                <p className={`font-semibold mb-2 ${isDark ? 'text-orange-300' : 'text-orange-800'}`}>
                  EOL NOTICE (Effective 1 February 2026)
                </p>
                <p className={`text-sm mb-3 ${isDark ? 'text-orange-200/80' : 'text-orange-700'}`}>
                  We're retiring legacy SweetPilot products (SweetPilot Prompts in SweetPilot Plus, SweetPilot Bots in SweetPilot Max, SweetPilot Add-ons, and the legacy Employees) to keep the platform simpler, faster, and easier to maintain. On February 1st, 2026, legacy products will reach their end-of-life and will no longer be available to use. Thanks for building with us!
                </p>
                <p className={`text-sm ${isDark ? 'text-orange-200/80' : 'text-orange-700'}`}>
                  If you're not sure what you're using or what to switch to, contact us at{' '}
                  <a
                    href="mailto:help@sweetpilot.com"
                    className={`underline ${isDark ? 'text-orange-300 hover:text-orange-200' : 'text-orange-800 hover:text-orange-900'}`}
                  >
                    help@sweetpilot.com
                  </a>
                  {' '}and we'll point you to the right option.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Overview Section */}
          <motion.section
            id="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-10"
          >
            <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Legacy products of SweetPilot AI include SweetPilot Bots (SweetPilot Max), SweetPilot Prompts (SweetPilot Plus), Add-ons, and the legacy version of SweetPilot Employees.
            </p>

            {/* Legacy Products Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`p-4 rounded-xl border text-center ${isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
                <MessageSquare className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Prompts</p>
              </div>
              <div className={`p-4 rounded-xl border text-center ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
                <Bot className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Bots</p>
              </div>
              <div className={`p-4 rounded-xl border text-center ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
                <Package className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Add-ons</p>
              </div>
              <div className={`p-4 rounded-xl border text-center ${isDark ? 'bg-pink-500/10 border-pink-500/20' : 'bg-pink-50 border-pink-200'}`}>
                <Wrench className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-pink-400' : 'text-pink-600'}`} />
                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Employees (Legacy)</p>
              </div>
            </div>
          </motion.section>

          {/* Accessing Prompts Section */}
          <motion.section
            id="accessing-prompts"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Accessing SweetPilot Prompts
            </h2>

            <ol className={`list-decimal list-inside space-y-3 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>
                Open{' '}
                <Link
                  to="/dashboard"
                  className={`underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  SweetPilot Dashboard
                </Link>
                {' '}and sign in. Contact support if you receive any errors at this step.
              </li>
              <li>Choose Prompts from the legacy section.</li>
              <li>Select the prompt card of your choice and follow the instructions on the page.</li>
            </ol>

            <div className={`p-4 rounded-xl border ${isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>Tip:</strong> Prompts are organized by category (Business, Marketing, Sales, etc.) making it easy to find what you need.
              </p>
            </div>
          </motion.section>

          {/* Plus Bundle Section */}
          <motion.section
            id="plus-bundle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              SweetPilot Plus Bundle
            </h2>

            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot Plus bundle includes access to more than 10,000 prompts for the following niches:
            </p>

            <div className={`p-4 rounded-xl border mb-4 ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex flex-wrap gap-2">
                {promptCategories.map((category) => (
                  <span
                    key={category}
                    className={`px-3 py-1 rounded-full text-sm ${
                      isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'
                    }`}
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>

            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              While the platform shows only 2,432 prompt cards, all of them have up to 5 different prompt versions inside, adding up to more than 10,000 prompts in total.
            </p>
          </motion.section>

          {/* Accessing Bots Section */}
          <motion.section
            id="accessing-bots"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Accessing SweetPilot Bots
            </h2>

            <ol className={`list-decimal list-inside space-y-3 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>
                Visit{' '}
                <Link
                  to="/dashboard"
                  className={`underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  SweetPilot Dashboard
                </Link>
                {' '}and sign in. Contact support if you receive any errors at this step.
              </li>
              <li>Select <em>View Bot</em> on the bot you would like to set up.</li>
              <li>Follow the link in step 2 of the setup guide. This step will also include a video walkthrough of the setup for that specific bot.</li>
            </ol>

            <div className={`p-4 rounded-xl border ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>Popular Bots:</strong> Find Competitors, Dropship Product Finder, Discord Event Bot, Agency Client Onboarding Bot, and many more.
              </p>
            </div>
          </motion.section>

          {/* Max Bundle Section */}
          <motion.section
            id="max-bundle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              SweetPilot Max Bundle
            </h2>

            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot Max Bundle was our premium legacy offering that included all bots, premium prompts, and exclusive automation templates. This bundle gave users access to the complete suite of legacy tools.
            </p>

            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The Max Bundle included:
            </p>

            <ul className={`space-y-2 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                <span>All SweetPilot Bots with ready-made setup guides</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                <span>Premium automation templates for various business needs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                <span>Priority support and video walkthroughs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                <span>Exclusive access to new bot releases</span>
              </li>
            </ul>

            <div className={`p-4 rounded-xl border ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>Upgrade to SweetPilot App:</strong> The functionality of the Max Bundle has been integrated into our new{' '}
                <Link
                  to="/dashboard/employees"
                  className={`underline ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-700 hover:text-green-800'}`}
                >
                  AI Employees
                </Link>
                {' '}platform, offering even more powerful automation capabilities.
              </p>
            </div>
          </motion.section>

          {/* Add-ons Section */}
          <motion.section
            id="add-ons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Add-ons
            </h2>

            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              A few examples of the Add-ons we offer:
            </p>

            <ul className={`space-y-2 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <span>400+ ADVANCED Prompts & 30+ Hacks To Supercharge Your Business</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <span>499+ AI Tools for any business category</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <span>30-day Ultimate ChatGPT Crash Course</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <span>Visual Content Creation Templates For Midjourney</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`}></span>
                <span>149+ Successful Brands to Spy on</span>
              </li>
            </ul>

            <div className={`p-4 rounded-xl border ${isDark ? 'bg-pink-500/10 border-pink-500/20' : 'bg-pink-50 border-pink-200'}`}>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>SweetPilot Hacks</strong> is a bundle of all add-ons we offer. These and other Add-ons are listed in the Add-ons section of our Legacy page.
              </p>
            </div>
          </motion.section>

          {/* Troubleshooting Section */}
          <motion.section
            id="troubleshooting"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Troubleshooting Access Issues
            </h2>

            <div className={`p-5 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
                  <HelpCircle className={`w-6 h-6 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                </div>
                <div>
                  <p className={`mb-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    If you have trouble accessing the purchased legacy products, please check the email address associated with your purchase in your order confirmation letter and reach out to our customer care team for assistance.
                  </p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                    Unfortunately, legacy products will not be available after their end-of-life date at February 1st, 2026. You can download some of them for local use before this date.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Divider */}
          <hr className={`my-10 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

          {/* Related Articles */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-10"
          >
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={article.title}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1
                      ? isDark ? 'border-b border-white/5' : 'border-b border-slate-100'
                      : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>{article.title}</span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>
          </motion.section>

          {/* Feedback */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className={`text-center p-8 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
          >
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Did this answer your question?
            </p>
            <div className="flex justify-center gap-4">
              <button className="text-3xl hover:scale-110 transition-transform" title="Not helpful">
                😞
              </button>
              <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat helpful">
                😐
              </button>
              <button className="text-3xl hover:scale-110 transition-transform" title="Very helpful">
                😃
              </button>
            </div>
          </motion.div>
        </div>

        {/* Sidebar TOC */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <nav className={`border-l ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                    activeSection === section.id
                      ? isDark
                        ? 'text-white border-l-2 border-purple-500 -ml-px bg-purple-500/10'
                        : 'text-purple-600 border-l-2 border-purple-600 -ml-px bg-purple-50'
                      : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>
      </StaticPageLayout>
    </>
  );
}
